import { observer, inject } from 'mobx-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStarOfLife, faArrowLeft, faUpload, faTrash, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Table from '../../components/table/Table';
import useLanguageChange from '../../utils/hooks/useLanguageChange';
import './credit-card-transactions.scss';
import Toggle from '../../components/toggle/toggle';
import Dialog from '../../components/dialog/dialog';
import UploadFileDragAndDrop from '../../components/upload-file-drag-and-drop/upload-file-drag-and-drop';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/loading-spinner/loading-spinner';
import {
  useCreditCardStatement,
  useTransactionFilter,
  useExpenseMatching,
  useUploadsWithAutoMatch,
  useTransactionSelectionAndSubmission,
  useTransactionViewer,
} from './hooks';
import Transaction from '../../components/transaction';
import { useMemo, useState } from 'react';
import { downloadFile } from '../../utils/file-manager';
import { fetch as apiFetch } from '../../utils/api-wrapper';
import Modal from '../../components/modal.js';
import Invoice from '../../components/invoice';
import UnmatchedExpensesTable from './unmatched-expenses-table/unmatched-expenses-table.jsx';
import ExpenseReceiptPdfViewer from './expense-receipt-pdf-viewer.jsx';

const baseColumnNames = {
  matched_expense_name: 'cc_transactions.column_names.matched_expense_name',
  amount: 'cc_transactions.column_names.amount',
  authorized_date: 'cc_transactions.column_names.authorized_date',
  iso_currency_code: 'cc_transactions.column_names.currency_code',
  merchant_name: 'cc_transactions.column_names.merchant_name',
  name: 'cc_transactions.column_names.transaction_name',
  pending: 'cc_transactions.column_names.pending',
  date: 'cc_transactions.column_names.date',
};
const columnNameKeys = {
  matched_expense: 'cc_transactions.column_names.matched_expense',
  review_transaction: 'cc_transactions.column_names.review_transaction',
  submitted: 'cc_transactions.column_names.submitted',
  ...baseColumnNames,
};

const pCardColumnNames = {
  matched_expense: 'cc_transactions.column_names.matched_expense',
  submitted: 'cc_transactions.column_names.submitted',
  ...baseColumnNames,
};

const INVOICE_TRANSACTION_SYNONYMS = new Set(['invoice', 'p-card']);

const CreditCardTransactions = ({ data }) => {
  useLanguageChange(data.t);
  const { statement_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [viewingExpense, setViewingExpense] = useState(null);
  const [expensePdfUrl, setExpensePdfUrl] = useState(null);
  const [isFetchingExpensePdf, setIsFetchingExpensePdf] = useState(false);
  const [showDeleteTransactionConfirmation, setShowDeleteTransactionConfirmation] = useState(false);

  const { statement, loading, mappedTransactions, unmatchedExpenses, allExpensesCompleted, fetchStatement, handleExpenseDrop } =
    useCreditCardStatement(statement_id, data.t, navigate);

  const handleBackNavigation = () => {
    // Preserve the account mask filter from URL params when navigating back
    const accountMaskFromUrl = searchParams.get('accountMask');
    if (accountMaskFromUrl) {
      navigate(`/c_card/statements?accountMask=${accountMaskFromUrl}`);
    } else {
      navigate('/c_card/statements');
    }
  };
  const { showPending, handleShowPendingToggle, displayedTransactions, amountTotal, hideSubmitted, handleHideSubmittedTransactionsToggle } = useTransactionFilter(mappedTransactions);
  const orderedDisplayedTransactions = useMemo(
    () =>
      (displayedTransactions || []).slice().sort((a, b) => {
        const aDate = new Date(a.date || a.authorized_date || 0);
        const bDate = new Date(b.date || b.authorized_date || 0);
        return bDate - aDate;
      }),
    [displayedTransactions]
  );
  const { hasUnprocessedCompletedExpenses } = useExpenseMatching(data.t);

  const { dialogIsOpen, openUploadModal, closeDialog, submitFiles, isAutoRefreshingActive } = useUploadsWithAutoMatch({
    statementId: statement_id,
    t: data.t,
    getDefaultTransactionType: () => statement?.default_transaction_type || 'expense_invoice',
    fetchStatement,
    hasUnprocessedCompletedExpenses,
  });

  // extracted hooks
  const { currentTransaction, handleRowClick, closeTransactionDialog } = useTransactionViewer({ fetchStatement, mappedTransactions });
  const { selectedRows, handleRowSelect, submitSelectedTransactions, isSubmittingSelected, someTransactionsAreAlreadySubmitted } =
    useTransactionSelectionAndSubmission({
      mappedTransactions,
      fetchStatement,
      statement,
      statement_id,
      t: data.t,
    });

  const closeViewingExpense = () => {
    setViewingExpense(null);
    if (expensePdfUrl) {
      // Revoke object URL to free memory
      URL.revokeObjectURL(expensePdfUrl);
      setExpensePdfUrl(null);
    }
  };

  const deleteSelectedTransactions = () => {
    const transaction_ids = selectedRows.map((tr) => tr._id);
    apiFetch(
      '/c_card/transactions/delete',
      'POST',
      { transaction_ids, statement_id },
      (response) => {
        toast.success(data.t.read('cc_transactions.delete_successful') || 'Transactions deleted successfully');
        fetchStatement();
      },
      () => {
        toast.error(data.t.read('cc_transactions.delete_failed') || 'Failed to delete transactions');
      }
    );
    setShowDeleteTransactionConfirmation(false);
  };

  const getTransactionType = () => {
    if (currentTransaction?.json?.type) {
      return currentTransaction.json.type;
    }
    if (currentTransaction?.transaction_type) {
      return currentTransaction.transaction_type;
    }
    return statement?.default_transaction_type || 'expense_invoice';
  };

  return (
    <>
      {viewingExpense && (
        <Dialog isOpen={!!viewingExpense} onClose={closeViewingExpense} additionnalStyles='dialog-padding-5'>
          <div className='expense-viewer-dialog'>
            <h5 className='mb-5'>{viewingExpense.name || data.t.read('cc_transactions.receipt') || 'Receipt'}</h5>
            {isFetchingExpensePdf && (
              <div className='d-flex align-items-center gap-2'>
                <LoadingSpinner />
                <span>{data.t.read('loading') || 'Loading...'}</span>
              </div>
            )}
            {!isFetchingExpensePdf && expensePdfUrl && (
              <ExpenseReceiptPdfViewer
                expense={viewingExpense}
                pdfBuff={expensePdfUrl}
                data={data}
                downloadFile={downloadFile}
              />
            )}
            {!isFetchingExpensePdf && !expensePdfUrl && (
              <div className='text-muted small'>{data.t.read('no_preview_available') || 'No preview available.'}</div>
            )}
          </div>
        </Dialog>
      )}
      <Dialog isOpen={dialogIsOpen} onClose={closeDialog}>
        <UploadFileDragAndDrop
          submitFiles={submitFiles}
          statementDefaultTransactionType={statement?.default_transaction_type || 'expense_invoice'}
        />
      </Dialog>
      {currentTransaction && (
        <Dialog isOpen={!!currentTransaction} additionnalStyles='dialog-padding-5'>
          {INVOICE_TRANSACTION_SYNONYMS.has(getTransactionType()) && currentTransaction?.invoice_id ? (
            <Invoice id={currentTransaction?.invoice_id} invoices={[]} close={closeTransactionDialog} />
          ) : (
            <Transaction
              data={data}
              statement_id={statement_id}
              statementData={statement}
              selected={currentTransaction}
              close={closeTransactionDialog}
              account_status={statement?.account_status || ''}
              card_type={statement?.card_type}
            />
          )}
        </Dialog>
      )}
      <div className='card p-1 mt-2 rounded-lg transaction-card'>
        <h2 className='ml-4 mt-4'>
          <FontAwesomeIcon icon={faArrowLeft} className='mr-2 go-back' onClick={handleBackNavigation} />
          {data.t.read('cc_transactions.title')}
          {statement && (
            <span className='statement-cycle'>
              {statement?.start_date} → {statement?.end_date}
            </span>
          )}
        </h2>
        <div className='card-body'>
          {!loading || isAutoRefreshingActive ? (
            <>
              <div className='table m-0 p-0'>
                <UnmatchedExpensesTable
                  unmatchedExpenses={unmatchedExpenses}
                  refreshStatementData={fetchStatement}
                  isLoading={loading}
                  statementId={statement_id}
                />
                {unmatchedExpenses && unmatchedExpenses.length > 0 && <h3 className='mb-5'>{data.t.read('cc_transactions.sub-title')}</h3>}
                <div className='buttons'>
                  <div className='d-flex align-items-center' style={{ minHeight: '29px' }}>
                    <Toggle isOn={showPending} handleToggle={handleShowPendingToggle} label={data.t.read('cc_transactions.show_pending')} />
                  </div>
                  <div className='d-flex align-items-center' style={{ minHeight: '29px' }}>
                    <Toggle isOn={hideSubmitted} handleToggle={handleHideSubmittedTransactionsToggle} label={data.t.read('cc_transactions.hide_submitted')} />
                  </div>
                  <div className='right-side-buttons'>
                    <div className='top-buttons'>
                      <button
                        className={`btn ${
                          selectedRows && selectedRows.length > 0 && !isSubmittingSelected ? 'btn-success' : 'btn-secondary disabled'
                        }`}
                        disabled={!selectedRows || selectedRows.length === 0 || isSubmittingSelected}
                        onClick={submitSelectedTransactions}
                      >
                        <FontAwesomeIcon icon={faClipboardCheck} />
                        {isSubmittingSelected
                          ? data.t.read('cc_transactions.submitting_selected') || 'Submitting...'
                          : data.t.read('cc_transactions.submit_selected_transactions') || 'Submit selected transactions'}
                      </button>
                      <button
                        disabled={!selectedRows || selectedRows.length === 0 || someTransactionsAreAlreadySubmitted}
                        className='btn btn-danger'
                        onClick={() => setShowDeleteTransactionConfirmation(true)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        {data.t.read('cc_transactions.delete_transactions')}
                      </button>
                      <button className='btn btn-primary' onClick={openUploadModal}>
                        <FontAwesomeIcon icon={faUpload} />
                        {data.t.read('cc_transactions.upload_receipts')}
                      </button>
                    </div>
                    <div style={{ minHeight: '20px' }}>
                      {someTransactionsAreAlreadySubmitted && (
                        <span className='text-danger small'>{data.t.read('cc_transactions.some_transactions_already_submitted')}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <div className='amount-container'>
                    {statement?.account_mask && (
                      <div>
                        {data.t.read('cc_transactions.column_names.account_mask')} : <span>{statement?.account_mask}</span>
                      </div>
                    )}
                    {statement?.card_type && (
                      <div>
                        {data.t.read('cc_statements.column_names.card_type')} : <span>{statement?.card_type}</span>
                      </div>
                    )}
                    <div>
                      {data.t.read('invoice.total')} : <span>{amountTotal} $</span>
                    </div>
                  </div>
                  {!allExpensesCompleted && (
                    <div className='d-flex justify-content-end align-items-center'>
                      <div className='processing-expenses-inline'>
                        <LoadingSpinner />
                        <p>{data.t.read('cc_transactions.processing_expenses') || 'Processing expenses...'}</p>
                      </div>
                    </div>
                  )}
                </div>
                <Table
                  columnNames={statement?.card_type === 'p-card' ? pCardColumnNames : columnNameKeys}
                  tableData={orderedDisplayedTransactions}
                  searchPlaceholder={data.t.read('cc_transactions.search_placeholder')}
                  onExpenseDrop={(expense, transaction) => handleExpenseDrop(expense, transaction)}
                  onRowClick={(transaction) => handleRowClick(transaction)}
                  selectableRows={true}
                  selectedRows={selectedRows}
                  selectableRowsCondition={(transaction) => !transaction.raw_transaction.submitted}
                  onRowSelect={handleRowSelect}
                  isLoading={loading}
                />
              </div>
              <>
                <FontAwesomeIcon size='xs' icon={faStarOfLife} className='mr-2' />
                {data.t.read('cc_transactions.date_might_differ')}
              </>
            </>
          ) : (
            <div className='d-flex loading-container'>
              <LoadingSpinner />
            </div>
          )}
        </div>
      </div>
      {showDeleteTransactionConfirmation && (
        <Modal
          width='50vw'
          title={data.t.read('cc_transactions.delete_transactions')}
          cancelAction={() => setShowDeleteTransactionConfirmation(false)}
          okAction={deleteSelectedTransactions}
          okText={data.t.read('delete')}
          cancelText={data.t.read('cancel')}
        >
          <p>{`${data.t.read('cc_transactions.delete_transactions_confirm')} (${selectedRows.length})${data.t.read(
            'cc_transactions.delete_transactions_confirm2'
          )}`}</p>
        </Modal>
      )}
    </>
  );
};

export default inject('data')(observer(CreditCardTransactions));
